import classNames from 'classnames';
import { FC, HTMLAttributes, ReactNode, useRef } from 'react';
import { useAuth } from '../../../context/authContext';
import useAsideStatus from '../../../hooks/useAsideStatus';
import useDomRect from '../../../hooks/useDomRect';
import { CalendarDate } from '../../../pages/patient/Calendar';
import Button from '../../ui/Button';
import NotificationDropdown from '../../ui/NotificationDropdown';
import { useResourceUsage } from '../../../context/ResourceUsageContext';
import UsageDropdown from '../../ui/UsageDropdown';
import { USAGE_LIMITS, PlanTier } from './usageLimits';


interface IHeaderLeftProps extends HTMLAttributes<HTMLDivElement> {
	children?: ReactNode;
	className?: string;
}
export const HeaderLeft: FC<IHeaderLeftProps> = (props) => {
	const { children, className, ...rest } = props;

	const { usage } = useResourceUsage();
	const { notesCount, formsCount, chatCount, planName, renewalDate } = usage;
	const limits = USAGE_LIMITS[planName as PlanTier] ?? { notes: 0, forms: 0, aiPrompts: 0 };
	const { setAsideStatus } = useAsideStatus();

	return (
		<div
			data-component-name='Header/HeaderLeft'
			className={classNames(
				'flex items-center gap-4',
				'ltr:md:mr-auto rtl:md:ml-auto',
				className,
			)}
			{...rest}>
			{/* Mobile: hamburger to open sidebar */}
			<button
				className='md:hidden rounded-full bg-gray-100 p-2'
				aria-label='Open sidebar'
				onClick={() => setAsideStatus(true)}
			>
				<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor' className='h-5 w-5'>
					<path d='M3.75 6.75A.75.75 0 014.5 6h15a.75.75 0 010 1.5h-15a.75.75 0 01-.75-.75zM3.75 12a.75.75 0 01.75-.75h15a.75.75 0 010 1.5h-15A.75.75 0 013.75 12zm0 5.25a.75.75 0 01.75-.75h15a.75.75 0 010 1.5h-15a.75.75 0 01-.75-.75z' />
				</svg>
			</button>

			{/* Desktop/Tablet: full usage bar */}
			<span className='hidden md:flex flex-wrap items-center gap-2 rounded-xl border border-zinc-200 bg-white/70 px-5 py-2 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/80'>
				{/* Plan badge with icon */}
				<span
					className='mr-3 flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold shadow'
					style={{
						background: 'linear-gradient(90deg, #08203E 0%, #557C93 100%)',
						color: '#fff',
						borderColor: '#08203E',
					}}>
					<svg width='16' height='16' fill='none' viewBox='0 0 16 16'>
						<circle cx='8' cy='8' r='8' fill='#fff' fillOpacity='0.2' />
						<path
							d='M5 8l2 2 4-4'
							stroke='#fff'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
					</svg>
					{planName}
				</span>

				<span className='font-bold tracking-wide text-zinc-700 dark:text-zinc-100'>
					Usage:
				</span>

				<span className='rounded-lg bg-blue-100 px-2 py-1 text-sm font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300'>
					{notesCount}/{limits.notes} Notes
				</span>

				<span className='hidden font-bold text-zinc-400 sm:inline'>|</span>

				<span className='rounded-lg bg-green-100 px-2 py-1 text-sm font-medium text-green-700 dark:bg-green-900 dark:text-green-300'>
					{formsCount}/{limits.forms} WSIB/MVA Forms
				</span>

				<span className='hidden font-bold text-zinc-400 sm:inline'>|</span>

				<span className='rounded-lg bg-purple-100 px-2 py-1 text-sm font-medium text-purple-700 dark:bg-purple-900 dark:text-purple-300'>
					{chatCount}/{limits.aiPrompts} AI Chat Prompts
				</span>

				<span className='hidden font-bold text-zinc-400 sm:inline'>—</span>

				<span className='rounded-lg bg-orange-100 px-2 py-1 text-sm font-medium text-orange-700 dark:bg-orange-900 dark:text-orange-300'>
					Renews on {renewalDate}
				</span>
			</span>
			{children}
		</div>
	);
};
HeaderLeft.displayName = 'HeaderLeft';

interface IHeaderRightProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	className?: string;
}
export const HeaderRight: FC<IHeaderRightProps> = (props) => {
	const { children, className, ...rest } = props;

	const { userData } = useAuth();

	return (
		<div
			data-component-name='Header/HeaderRight'
			className={classNames(
				'flex items-center gap-4',
				'ltr:md:ml-auto rtl:md:mr-auto',
				className,
			)}
			{...rest}>
			{/* Mobile: show usage button on the right to keep left side clear */}
			<div className='md:hidden'>
				<UsageDropdown />
			</div>
				<CalendarDate/>
				<NotificationDropdown/>
		</div>
	);
};
HeaderRight.displayName = 'HeaderRight';

interface IHeaderProps {
	children: ReactNode;
	className?: string;
}
const Header: FC<IHeaderProps> = (props) => {
	const { children, className, ...rest } = props;

	const divRef = useRef<HTMLDivElement>(null);
	const [domRect] = useDomRect(divRef);

	return (
		<>
			<style>{`:root {--header-height: ${domRect?.height || 0}px}`}</style>
			<header
				ref={divRef}
				data-component-name='Header'
				className={classNames(
					'sticky top-0 z-10',
					'flex items-center justify-between gap-4',
					'border-b border-zinc-300/25 bg-white',
					'p-6',
					'backdrop-blur-md',
					'dark:border-zinc-800/50 dark:bg-zinc-900/75 dark:text-white',
					'z-40',
					className,
				)}
				{...rest}>
				{children}
			</header>
		</>
	);
};
Header.displayName = 'Header';

export default Header;
